import {
  Button,
  chakra,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import { FormSelect } from "../../../../components/Forms";
import {
  CSRD_QUESTIONNAIRE_TYPES,
  generateArrayOfYears,
  showErrorToast
} from "../../../../utils";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useGetOrganizationUsers } from "../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useEffect } from "react";
import { sortUsersByEmail } from "../../../../utils/userSort";

const mapDataToFormData = (data) => {
  return {
    questionnaireType: data?.originalType ?? "",
    assignee: data?.assignee ?? "",
    year: data?.year ?? ""
  };
};

function CSRDPlanningModal({ modalData, isOpen, onClose, onSubmit }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning"
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({});

  useEffect(() => {
    if (isOpen) {
      if (modalData) {
        // Editing existing task
        reset(mapDataToFormData(modalData));
      } else {
        // Creating new task
        reset(mapDataToFormData());
      }
    }
  }, [isOpen, modalData, reset]);

  const {
    data: users,
    isLoading: isUsersLoading
  } = useGetOrganizationUsers({
    onError: err => {
      showErrorToast(toast, err.message);
      onClose();
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  useEffect(() => {
    if (isUsersLoading) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isUsersLoading]);

  const prepareSubmitData = (data) => {
    const dataToSave = {
      year: data.year,
      type: data.questionnaireType,
      assignee: data.assignee
    };
    onSubmit(modalData?.id, dataToSave);
  };

  return !isOpen || isUsersLoading ?
    null : <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
          <ModalHeader>{modalData ? t("modal.editTaskHeader") : t("modal.createTaskHeader")}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Heading size="md" marginBlockEnd="2">{t("modal.generalData")}</Heading>
            <Flex direction="column" gap="2">
              <FormSelect
                label={`${t("modal.questionnaireType")} *`}
                placeholder=" "
                isDisabled={modalData != null}
                errors={errors}
                {...register("questionnaireType", {
                  required: globalTranslation("entityRequired", { entityName: t("modal.questionnaireType") })
                })}
              >
                {Object.values(CSRD_QUESTIONNAIRE_TYPES).map(val =>
                  <option key={val} value={val}>{globalTranslation(`csrd.questionnaireTypes.${val}`)}</option>
                )}
              </FormSelect>

              <FormSelect
                label={`${t("modal.assignee")} *`}
                placeholder=" "
                errors={errors}
                {...register("assignee", {
                  required: globalTranslation("entityRequired", { entityName: t("modal.assignee") })
                })}
              >
                {users ? sortUsersByEmail(users).map(assignee =>
                  <option key={assignee.id} value={assignee.email}>
                    {assignee.email}
                  </option>
                ) : []}
              </FormSelect>
            </Flex>

            <Heading size="md" marginBlockStart="10" marginBlockEnd="2">{t("modal.selectPeriod")}</Heading>
            <Flex direction="column" gap="2">
              <FormSelect
                label={`${t("modal.financialYear")} *`}
                type="text"
                errors={errors}
                isDisabled={modalData != null}
                {...register("year",
                  { required: globalTranslation("entityRequired", { entityName: t("modal.financialYear") }) })}
              >
                {generateArrayOfYears().map(year => <option key={year} value={year}>{year}</option>)}
              </FormSelect>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t("modal.close")}
            </Button>
            <Button colorScheme="customScheme" type="submit">
              {t("modal.save")}
            </Button>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal>;
}

export default CSRDPlanningModal;